import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { Row, Col, Modal } from "react-bootstrap";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { apiAxios } from "../../../../utils/apiAxios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Role {
  roleID: number;
  roleName: string;
  isCentralHub: boolean;
}

/* NEW: SLA TYPE INTERFACE */
interface SlaType {
  slaTypeID: number;
  slaTypeName: string;
}

interface SLAItem {
  slaType: string;
  slaid: number;
  roleID: number;
  slaTypeID: number; 
  slaTimeLimit: number;
  isActive: boolean;
}

const AddSLA: React.FC = () => {

  const [roles, setRoles] = useState<Role[]>([]);
  const [data, setData] = useState<SLAItem[]>([]);
  const [roleID, setRoleID] = useState("");
  const [slaTime, setSlaTime] = useState("");
  const [editId, setEditId] = useState<number | null>(null);

  /* NEW STATES */
  const [slaTypes, setSlaTypes] = useState<SlaType[]>([]);
  const [slaTypeID, setSlaTypeID] = useState("");

  const [search, setSearch] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const token = sessionStorage.getItem("token") || "";

  useEffect(() => {
    fetchRoles();
    fetchSLA();
    fetchSlaTypes();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await apiAxios.get(`${API_BASE}/Users/GetRoles`, {
        headers: { AuthorizationData: token },
      });
      setRoles(res.data);
    } catch {
      toast.error("Failed to load roles");
    }
  };

  /* NEW: FETCH SLA TYPES BASED ON ROLE */
const fetchSlaTypes = async () => {
  try {
    const res = await apiAxios.get(`${API_BASE}/AddSLA/GetAllSlaTypes`, {
      headers: { AuthorizationData: token },
    });

    if (res.data?.isSuccess) {
      setSlaTypes(res.data.singleData);
    }
  } catch {
    toast.error("Failed to load SLA Types");
  }
};


  const fetchSLA = async () => {
    try {
      const res = await apiAxios.get(`${API_BASE}/AddSLA/GetAll`, {
        headers: { AuthorizationData: token },
      });

      if (res.data?.isSuccess) {
        setData(res.data.singleData);
      }
    } catch {
      toast.error("Failed to load SLA list");
    }
  };

  const getRoleName = (roleID: number) => {
    return roles.find((r) => r.roleID === roleID)?.roleName || "N/A";
  };

  /* NEW: GET SLA TYPE NAME */
  const getSlaTypeName = (slaTypeID: number) => {
    return slaTypes.find((s) => s.slaTypeID === slaTypeID)?.slaTypeName || "N/A";
  };

  const handleSave = async () => {
    if (!roleID || !slaTypeID || !slaTime) {
      toast.error("All fields required");
      return;
    }

    const payload = {
      slaid: editId ?? 0,
      roleID: Number(roleID),
      slaTypeID: Number(slaTypeID),
      slaTimeLimit: Number(slaTime),
    };

    try {
      const url = editId
        ? `${API_BASE}/AddSLA/Update`
        : `${API_BASE}/AddSLA/Create`;

      const method = editId ? apiAxios.put : apiAxios.post;

      const res = await method(url, payload, {
        headers: { AuthorizationData: token },
      });

      if (res.data?.isSuccess) {
        toast.success(res.data.status);
        fetchSLA();
        clearForm();
      } else {
        toast.error(res.data.status);
      }
    } catch {
      toast.error("SLA already exists");
    }
  };

  const clearForm = () => {
    setRoleID("");
    setSlaTypeID("");
    setSlaTime("");
    setEditId(null);
  };

  const handleEdit = (row: SLAItem) => {
    setEditId(row.slaid);
    setRoleID(String(row.roleID));
    setSlaTypeID(String(row.slaTypeID));
    setSlaTime(String(row.slaTimeLimit));


    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const res = await apiAxios.delete(
        `${API_BASE}/AddSLA/Delete/${deleteId}`,
        { headers: { AuthorizationData: token } }
      );

      if (res.data?.isSuccess) {
        toast.success(res.data.status);
        fetchSLA();
      } else {
        toast.error(res.data.status);
      }
    } catch {
      toast.error("Delete failed");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const filteredData = data.filter((x) =>
    getRoleName(x.roleID)
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const columns: TableColumn<SLAItem>[] = [
    {
      name: "SLAID",
      selector: (r) => r.slaid,
      width: "80px",
      center: true,
    },
    {
      name: "Role",
      selector: (r) => getRoleName(r.roleID),
      sortable: true,
    },
    /* NEW COLUMN */
    {
      name: "SLA Type",
      selector: (r) => `${r.slaType}`,
      sortable: true,
      wrap:true,
    },
    {
      name: "SLATimeLimit",
      selector: (r) => `${r.slaTimeLimit} min`,
      center: true,
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="action-btn edit"
            onClick={() => handleEdit(row)}
          >
            <i className="bi bi-pencil-square"></i>
          </button>

          <button
            className="action-btn delete"
            onClick={() => confirmDelete(row.slaid)}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <h4 className="mb-4">Add SLA</h4>

      <div className="premium-card rounded p-4">
        <Row>
          <Col md={4} className="mt-1">
            <label className="form-label">
              Role <span className="text-danger">*</span>
            </label>

            <select
              className="form-select"
              value={roleID}
             onChange={(e) => setRoleID(e.target.value)}
            >
              <option value="">Select Role</option>
              {roles.map((r) => (
                <option key={r.roleID} value={r.roleID}>
                  {r.roleName}
                </option>
              ))}
            </select>
          </Col>

          {/* NEW SLA TYPE DROPDOWN */}
          <Col md={4} className="mt-1">
            <label className="form-label">
              SLA Type <span className="text-danger">*</span>
            </label>

            <select
              className="form-select"
              value={slaTypeID}
              onChange={(e) => setSlaTypeID(e.target.value)}
            >
              <option value="">Select SLA Type</option>

              {slaTypes.map((s) => (
                <option key={s.slaTypeID} value={s.slaTypeID}>
                  {s.slaTypeName}
                </option>
              ))}
            </select>
          </Col>

          <Col md={4} className="mt-1">
            <label className="form-label">
              SLATimeLimit <span className="text-danger">*</span>
            </label>

            <input
              type="number"
              className="form-control"
              min="1"
              value={slaTime}
              onChange={(e) => setSlaTime(e.target.value)}
            />
          </Col>
        </Row>

        <div className="mt-3 d-flex gap-2">
          <button className="btn btn-primary" onClick={handleSave}>
            {editId ? "Update" : "Save"}
          </button>

          <button className="btn btn-secondary" onClick={clearForm}>
            Clear
          </button>
        </div>
      </div>

      {/* TABLE */}

      <div className="premium-card mt-3 ">
             <h5 className="mb-3">SLA List</h5>
        <div className="row">
        <div className="col-md-12 col-xl-3 col-12">
            <input
              className="form-control mb-3"
              placeholder="Search role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          noDataComponent="No records found"
             className="admin-delivery-table"
        />
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
      >
        <Modal.Body className="text-center">
          Are you sure you want to delete this SLA?
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <button className="btn btn-primary" onClick={handleDelete}>
            Yes
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            No
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddSLA;